import { parseRegisteredRobots } from '@/api/FiloApiClient';
import { readBatteryFields, type RobotBatteryFields } from '@/api/robotBattery';
import type { RegisteredRobot } from '@/api/types';
import type { DirectoryEvent } from './RobotDirectoryClient';

export class RobotDirectoryRevision {
  private value = 0;

  capture() {
    return this.value;
  }

  recordDirectoryUpdate() {
    this.value += 1;
  }

  isCurrent(captured: number) {
    return captured === this.value;
  }

  applyIfCurrent(captured: number, apply: () => void) {
    if (!this.isCurrent(captured)) return false;
    apply();
    return true;
  }
}

export class RobotStatusEventOrder {
  private latestByRobotId = new Map<string, {
    time: number;
    online: boolean;
    occurredAt: string;
    battery: RobotBatteryFields;
  }>();

  mergeSnapshot(
    snapshot: RegisteredRobot[],
    occurredAt: unknown,
  ): RegisteredRobot[] {
    const snapshotTime = typeof occurredAt === 'string' ? Date.parse(occurredAt) : Number.NaN;
    if (!Number.isFinite(snapshotTime)) throw new Error('Directory snapshot time is invalid');
    const snapshotRobotIds = new Set(snapshot.map(({ robotId }) => robotId));
    [...this.latestByRobotId.keys()].forEach((robotId) => {
      if (!snapshotRobotIds.has(robotId)) this.latestByRobotId.delete(robotId);
    });
    return snapshot.map((robot) => {
      const latest = this.latestByRobotId.get(robot.robotId);
      if (latest && latest.time > snapshotTime) {
        return {
          ...robot,
          online: latest.online,
          lastSeenAt: latest.occurredAt,
          batteryLevel: latest.battery.batteryLevel,
          isCharging: latest.battery.isCharging,
        };
      }
      this.latestByRobotId.set(robot.robotId, {
        time: snapshotTime,
        online: robot.online,
        occurredAt: robot.lastSeenAt ?? String(occurredAt),
        battery: { batteryLevel: robot.batteryLevel, isCharging: robot.isCharging },
      });
      return robot;
    });
  }

  acceptStatus(event: DirectoryEvent) {
    if (
      event.type !== 'robotStatus'
      || typeof event.robotId !== 'string'
      || typeof event.online !== 'boolean'
      || typeof event.occurredAt !== 'string'
    ) return false;
    const occurredAt = Date.parse(event.occurredAt);
    if (!Number.isFinite(occurredAt)) return false;
    const latest = this.latestByRobotId.get(event.robotId);
    if (latest && occurredAt <= latest.time) return false;
    this.latestByRobotId.set(event.robotId, {
      time: occurredAt,
      online: event.online,
      occurredAt: event.occurredAt,
      battery: readBatteryFields(event),
    });
    return true;
  }
}

export const applyRobotDirectoryEvent = (
  current: RegisteredRobot[],
  event: DirectoryEvent,
): RegisteredRobot[] => {
  if (event.type === 'directorySnapshot') return parseRegisteredRobots(event.robots);
  if (event.type !== 'robotStatus' || typeof event.robotId !== 'string' || typeof event.online !== 'boolean') {
    return current;
  }
  const eventTime = typeof event.occurredAt === 'string' ? Date.parse(event.occurredAt) : Number.NaN;
  if (!Number.isFinite(eventTime)) return current;
  const battery = readBatteryFields(event);
  return current.map((robot) => {
    if (robot.robotId !== event.robotId) return robot;
    const currentTime = robot.lastSeenAt ? Date.parse(robot.lastSeenAt) : Number.NaN;
    if (Number.isFinite(currentTime) && eventTime <= currentTime) return robot;
    return {
      ...robot,
      online: event.online!,
      lastSeenAt: event.occurredAt,
      batteryLevel: battery.batteryLevel,
      isCharging: battery.isCharging,
    };
  });
};
